import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { IndexedDbService } from './indexedDb.service';
import { BaseService } from './baseService';
import { ApiMode, ResourceType } from '../../utils/enums';
import { Observable, of, Subject } from 'rxjs';
import { flatMap, map, zip, catchError } from 'rxjs/operators';
import { Resource } from '../models';
import { AppConstants } from '../../utils/appConstants.class';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class OfflineApiService extends BaseService {
  constructor(private api: ApiService, private indexedDb: IndexedDbService) {
    super();
  }

  getWithMode(path: string, mode: ApiMode = ApiMode.Online, table?: string, key?: string): Observable<any> {
    if (mode === ApiMode.Online || this.isManager) {
      return this.api.get(path);
    }
    if (mode === ApiMode.OnlineSave) {
      return this.getOnlineSave(path, table, key);
    }
    if (mode === ApiMode.Lazy) {
      return this.getLazy(path, table, key);
    }
    if (mode === ApiMode.Offline) {
      return this.getOffline(table, key);
    }
  }

  postSyncList(path: string, body: any = {}, table: string, dateKey?: string) {
    if (!this.isConnection) {
      return this.indexedDb.readAll(table);
    }
    const refreshAllIfOlder = window['refreshAllIfOlder'];
    if (refreshAllIfOlder && body && body.lastSyncDate && body.lastSyncDate < refreshAllIfOlder) {
      // we need a full refresh because local data is out of date
      body.lastSyncDate = null;
    }
    return this.api.post(path, body).pipe(
      flatMap((data) => {
        localStorage.setItem(
          dateKey ? dateKey : `${table}${AppConstants.LAST_SYNC_DATE}_${this.applicationType}_${this.currentUserId}`,
          data.LastSyncDate
        );
        return this.indexedDb.mergeAll(table, data.Items).pipe(
          flatMap(() =>
            this.indexedDb.readAll(table).pipe(
              flatMap((items: any[]) => {
                const obs = [];
                items.forEach((i) => {
                  if (!data.Scope.includes(i.id)) {
                    obs.push(this.indexedDb.remove(table, i.id));
                  }
                });
                if (obs.length > 0) {
                  return of(null).pipe(
                    zip(...obs),
                    flatMap(() => this.indexedDb.readAll(table))
                  );
                }
                return this.indexedDb.readAll(table);
              })
            )
          )
        );
      })
    );
  }

  getOfflineWithoutSync(table: string): Observable<any[]> {
    return this.indexedDb.readAll(table);
  }

  getSyncUpdates(path: string, table: string, key: string, forceCheckUpdates = false) {
    if (!this.isConnection) {
      return this.indexedDb.read(table, key);
    }
    return this.getOffline(table, key).pipe(
      flatMap((data) => {
        if (data?.isOffline && data.offlinePersonId === this.currentUserId && data.lastSyncDate) {
          return of(data);
        }
        if ((data && data.lastSyncDate) || (forceCheckUpdates && data)) {
          return this.api.post(path, data.lastSyncDate).pipe(
            flatMap((res) => {
              if (table && key) {
                return this.indexedDb.merge(table, res, true).pipe(flatMap(() => this.indexedDb.read(table, key)));
              }
            })
          );
        } else if ((data && !data.lastSyncDate) || !data) {
          return this.api.get(path).pipe(
            map((res) => {
              if (table && key) {
                this.indexedDb.put(table, res);
              }
              return res;
            })
          );
        }
      })
    );
  }

  getResources(resources: Resource[], counter?: Subject<any>): Observable<any> {
    const obs = [];
    resources.forEach((r) => {
      obs.push(
        this._getResources(r).pipe(
          map((data) => {
            if (counter) {
              counter.next(1);
            }
            return data;
          })
        )
      );
    });
    return of(null).pipe(zip(...obs));
  }

  private getAsset(resource: Resource): Observable<any> {
    return this.api.getImage(resource.data.url).pipe(
      catchError((error: any) => {
        return of(null);
      }),
      map((res) => {
        return res ? this.indexedDb.putImage(resource.key, res) : of(null);
      })
    );
  }

  private getAttachment(resource: Resource): Observable<any> {
    return this.api.getFile(resource.data.url, resource.data.name, resource.data.type).pipe(
      catchError((error: any) => {
        return of(null);
      }),
      map((res) => {
        return res ? this.indexedDb.putImage(resource.key, res) : of(null);
      })
    );
  }

  private _getResources(resource: Resource): Observable<any> {
    switch (resource.resourceType) {
      case ResourceType.Asset:
      case ResourceType.FormFile:
        return this.getAsset(resource);

      case ResourceType.Attachment:
        return this.getAttachment(resource);
    }
  }

  private getOffline(table: string, key?: string): Observable<any> {
    if (table && key) {
      return this.indexedDb.read(table, key).pipe(
        map((res) => {
          return res;
        })
      );
    }
    if (table) {
      return this.indexedDb.readAll(table).pipe(
        map((res) => {
          return res;
        })
      );
    }
  }

  private getLazy(path: string, table?: string, key?: string): Observable<any> {
    return this.getOffline(table, key).pipe(
      flatMap((data) => {
        if (data) {
          return of(data);
        } else {
          return this.api.get(path).pipe(
            map((res) => {
              if (table && key) {
                this.indexedDb.put(table, res, key);
              } else {
                if (table && Array.isArray(res)) {
                  res.forEach((r) => this.indexedDb.put(table, r));
                }
              }
              return res;
            })
          );
        }
      })
    );
  }

  private getOnlineSave(path: string, table?: string, key?: string): Observable<any> {
    return this.api.get(path).pipe(
      map((data) => {
        if (table) {
          this.indexedDb.put(table, data, key).subscribe();
        }
        return data;
      })
    );
  }
}
